import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, Badge, EmptyState } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api from '../../utils/api';

const STATUS_BADGE = {
  processed: 'ok',
  failed: 'critical',
  pending: 'warning',
};

const STATUS_LABEL = {
  processed: 'Processed',
  failed: 'Failed',
  pending: 'Pending',
};

export default function B2BEmails() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [reprocessing, setReprocessing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const buildUrl = useCallback(() => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (statusFilter) params.set('status', statusFilter);
    return `/api/b2b/emails?${params}`;
  }, [page, statusFilter]);

  const { data, loading, error, refetch } = useFetch(buildUrl());

  const emails = data?.emails || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const handleFilterChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  const handleReprocess = async (email) => {
    if (!window.confirm(`Reprocess email from ${email.from_address}?\n\nThis will attempt to re-parse the PDF and create a new B2B order.`)) return;
    setReprocessing(email.id);
    setSuccessMsg(null);
    try {
      await api.post(`/api/b2b/emails/${email.id}/reprocess`);
      setSuccessMsg(`Reprocessing started for email #${email.id}. Refresh in a few seconds to see updated status.`);
      // Refresh after a short delay to let processing start
      setTimeout(() => refetch(), 3000);
    } catch (err) {
      alert(errMsg(err));
    } finally {
      setReprocessing(null);
    }
  };

  const toggleExpand = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div>
      <div className="page-header">
        <h1>B2B Inbound Emails</h1>
        <div className="page-actions">
          <select
            className="form-select form-select-sm"
            value={statusFilter}
            onChange={e => handleFilterChange(e.target.value)}
            style={{ minWidth: 130 }}
          >
            <option value="">All statuses</option>
            <option value="processed">Processed</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <button className="btn btn-sm" onClick={() => { refetch(); setSuccessMsg(null); }}>
            <i className="ti ti-refresh" /> Refresh
          </button>
        </div>
      </div>

      {successMsg && (
        <div className="alert alert-success mb-3" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="ti ti-circle-check" style={{ color: '#16a34a' }} />
          {successMsg}
          <button
            onClick={() => setSuccessMsg(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
          >×</button>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {!loading && !error && (
        <>
          <div style={{ marginBottom: 8, color: '#666', fontSize: 13 }}>
            Showing {emails.length} of {total} email{total !== 1 ? 's' : ''}
            {statusFilter ? ` · filtered by "${STATUS_LABEL[statusFilter] || statusFilter}"` : ''}
          </div>

          {emails.length === 0 ? (
            <EmptyState
              icon="ti-mail"
              title="No emails found"
              description={statusFilter ? 'Try changing the status filter.' : 'No B2B inbound emails have been received yet.'}
            />
          ) : (
            <div className="card p-0">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>Received</th>
                    <th>From</th>
                    <th>Subject</th>
                    <th style={{ width: 110 }}>Attachments</th>
                    <th style={{ width: 110 }}>Status</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map(email => (
                    <React.Fragment key={email.id}>
                      <tr
                        className={expanded === email.id ? 'table-active' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleExpand(email.id)}
                      >
                        <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {fmt.dateTime ? fmt.dateTime(email.received_at) : new Date(email.received_at).toLocaleString()}
                        </td>
                        <td style={{ fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {email.from_address}
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {email.subject || <span style={{ color: '#999' }}>(no subject)</span>}
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {email.file_names && email.file_names.length > 0 ? (
                            <span title={email.file_names.join(', ')}>
                              <i className="ti ti-paperclip" /> {email.file_names.length}
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>—</span>
                          )}
                        </td>
                        <td>
                          <Badge variant={STATUS_BADGE[email.status] || 'neutral'}>
                            {STATUS_LABEL[email.status] || email.status}
                          </Badge>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {(email.status === 'failed' || email.status === 'pending') && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleReprocess(email)}
                              disabled={reprocessing === email.id}
                              title="Re-parse PDF and create B2B order"
                            >
                              {reprocessing === email.id ? (
                                <><i className="ti ti-loader ti-spin" /> Processing…</>
                              ) : (
                                <><i className="ti ti-refresh" /> Reprocess</>
                              )}
                            </button>
                          )}
                          {email.status === 'processed' && (
                            <span style={{ color: '#16a34a', fontSize: 13 }}>
                              <i className="ti ti-circle-check" /> Done
                            </span>
                          )}
                        </td>
                      </tr>

                      {expanded === email.id && (
                        <tr>
                          <td colSpan={6} style={{ background: '#f8fafc', padding: '12px 20px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                              <div>
                                <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>Email Details</div>
                                <div style={{ fontSize: 13, color: '#444' }}>
                                  <div><strong>ID:</strong> #{email.id}</div>
                                  <div><strong>From:</strong> {email.from_address}</div>
                                  <div><strong>Subject:</strong> {email.subject || '(no subject)'}</div>
                                  <div><strong>Received:</strong> {new Date(email.received_at).toLocaleString()}</div>
                                  {email.file_names && email.file_names.length > 0 && (
                                    <div><strong>Files:</strong> {email.file_names.join(', ')}</div>
                                  )}
                                </div>
                              </div>
                              {email.error && (
                                <div>
                                  <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, color: '#dc2626' }}>
                                    Error Details
                                  </div>
                                  <div style={{
                                    fontSize: 12,
                                    color: '#dc2626',
                                    background: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    borderRadius: 6,
                                    padding: '8px 12px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}>
                                    {email.error}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button
                className="btn btn-sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <i className="ti ti-chevron-left" /> Prev
              </button>
              <span style={{ alignSelf: 'center', fontSize: 13 }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
